// Thin LOCAL fetch client for the THU-346 structured encounter-note endpoints (the FB-authorized
// synthetic demo clinical store). Deliberately NOT in the published SDK yet (local-first). Mirrors
// the SDK HttpClient shape: baseUrl + getToken → Bearer, credentials:"include" (dev cookie), unwraps
// the { data } envelope. No gated clinical terms here (subjective/objective/assessment/plan).
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

export interface EncounterNote {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  status: "draft" | "saved";
  updated_at?: string;
}
export type EncounterNoteInput = Omit<EncounterNote, "updated_at">;

export interface EncounterNoteApi {
  get(patientId: string): Promise<EncounterNote | null>;
  save(patientId: string, note: EncounterNoteInput): Promise<{ status: string; updated_at?: string }>;
}

export function useEncounterNoteApi(): EncounterNoteApi {
  const { getToken } = useAppAuth();
  return useMemo<EncounterNoteApi>(() => {
    async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
      const headers: Record<string, string> = {};
      if (body !== undefined) headers["Content-Type"] = "application/json";
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const err = (json as { error?: { code?: string; message?: string } }).error;
        throw new Error(err?.message ?? "Request failed");
      }
      return (json as { data?: T }).data as T;
    }
    return {
      get: (patientId) =>
        request<EncounterNote | null>(
          "GET",
          `/v1/doctor/patients/${encodeURIComponent(patientId)}/encounter-note`,
        ),
      save: (patientId, note) =>
        request<{ status: string; updated_at?: string }>(
          "PUT",
          `/v1/doctor/patients/${encodeURIComponent(patientId)}/encounter-note`,
          note,
        ),
    };
  }, [getToken]);
}
